"use client"

import axios from "axios"
import { useEffect, useRef, useState } from "react"
import { NotificationManager } from "react-notifications"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import DownArrow from "../../assets/svgs/down-arrow.svg"
import Plus from "../../assets/svgs/Plus.svg"
import ColumnCustomizer from "../../components/ColumnCustomizer"
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal"
import useAuth from "../../hooks/useAuth"
import BaseLayout from "../../layouts/BaseLayout"
import { BASE_URL } from "../../utils/Element"
import { initialLeadFilterData } from "../../utils/initialData"
import AddLeadModal from "./AddLeadModal"
import ConvertPipelineModal from "./components/ConvertPipelineModal"
import LeadAdvanceFilter from "./components/LeadAdvanceFilter"
import LeadsDashboard from "./components/LeadDashboard"
import EditLeadModal from "./EditLeadModal"

const Leads = () => {
  const [config] = useAuth()
  const navigate = useNavigate()

  // ─── Search + Debounce states ────────────────────────────────
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const debounceTimer = useRef(null)

  const [loading, setLoading] = useState(true)
  const [leadsData, setLeadsData] = useState([])
  const [selectedId, setSelectedId] = useState("")
  const [showColumnModal, setShowColumnModal] = useState(false)
  const [bdsOptions, setBdsOptions] = useState([])
  const [currentPage, setCurrentPage] = useState(1)
  const [itemPerPage, setItemPerPage] = useState(50)
  const [sortBy, setSortBy] = useState("id")
  const [leadDashboard, setLeadDashboard] = useState()
  const [sortDirection, setSortDirection] = useState("desc")
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [disableDelete, setDisableDelete] = useState(false)
  const [expandedRows, setExpandedRows] = useState({})
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [rowStatuses, setRowStatuses] = useState({})
  const [activeTab, setActiveTab] = useState(3)
  const [selectedLeadType, setSelectedLeadType] = useState(null)

  const [advanceFilterData, setAdvanceFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : []
  })

  const [pageSizes, setPageSizes] = useState({}) 

  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [filterStatus, setFilterStatus] = useState({
    this_week: false,
    lead_not_contacted: false,
    total_lead_ytd: false,
    new_lead_quarter: false,
  })

  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed)

  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  })

  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData")
    return localFilterData ? JSON.parse(localFilterData) : initialLeadFilterData
  })

  // ==================== COLUMN CUSTOMIZATION ====================
  const allColumns = [
    { key: 'details', label: 'Details', fixed: true },
    { key: 'lead_date', label: 'Lead Date', fixed: true },
    { key: 'contact_name', label: 'Contact Name' },
    { key: 'lead_type', label: 'Lead Type' },
    { key: 'property_acquisition', label: 'Property / Acquisition Criteria' },
    { key: 'lead_source', label: 'Lead Source' },
    { key: 'last_contacted', label: 'Last Contacted' },
    { key: 'actions', label: 'Actions', fixed: true },
  ]

  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('leadsVisibleColumns')
    return saved
      ? JSON.parse(saved)
      : allColumns.map(col => col.key)
  })
  // ============================================================

  const [selectedUser, setSelectedUser] = useState(null)
  const [originalUserData, setOriginalUserData] = useState(null)
  const [isSwitchingUser, setIsSwitchingUser] = useState(false)

  // ─── Debounce logic ───────────────────────────────────────────
  const handleSearchChange = (e) => {
    const value = e.target.value
    const trimmed = value.startsWith(" ") ? value.trimStart() : value
    setSearch(trimmed)
  }

  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    debounceTimer.current = setTimeout(() => {
      if (search.length === 0 || search.length >= 3) {
        setDebouncedSearch(search.trim())
      }
    }, 450)

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [search])

  const statusOptions = [
    { value: 2, label: "Cold" },
    { value: 3, label: "Warm" },
    { value: 1, label: "Hot" },
  ]

  const colorMap = { 1: "#ff0000", 3: "#ffb952", 2: "blue" }

  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}`

    if (debouncedSearch) {
      params += `&search=${encodeURIComponent(debouncedSearch)}`
    }

    const {
      bds, broker, lead_source, created_at, created_end_date, created_start_date,
      contact, lead_type, lead_type_ytd, link_id, property, buyer_id,
      last_contact, lastContactSentDate, lastContactSentEdate, lastContactSentSdate,
      within_last_day, longer_than_day, date_category
    } = filters

    if (bds) params += `&bds=${encodeURIComponent(bds)}`
    if (broker) params += `&broker=${encodeURIComponent(broker)}`
    if (lead_source?.length > 0) params += `&lead_source=${encodeURIComponent(lead_source)}`
    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`
    if (contact?.id) params += `&contact=${encodeURIComponent(contact.id)}`
    if (lead_type) params += `&lead_type=${encodeURIComponent(lead_type === "acquisition" ? 1 : 0)}`
    if (lead_type_ytd) params += `&lead_type_ytd=${encodeURIComponent(lead_type_ytd === "acquisition" ? 1 : 0)}`
    if (last_contact && (lastContactSentDate || (lastContactSentSdate && lastContactSentEdate))) {
      params += `&last_contact=${encodeURIComponent(last_contact)}`
    }
    if (lastContactSentDate) params += `&last_update_date=${encodeURIComponent(lastContactSentDate)}`
    if (lastContactSentSdate) params += `&last_update_start_date=${encodeURIComponent(lastContactSentSdate)}`
    if (lastContactSentEdate) params += `&last_update_end_date=${encodeURIComponent(lastContactSentEdate)}`
    if (within_last_day) params += `&within_last_day=${encodeURIComponent(within_last_day)}`
    if (longer_than_day) params += `&longer_than_day=${encodeURIComponent(longer_than_day)}`
    if (date_category !== undefined) params += `&date_category=${encodeURIComponent(date_category)}`
    if (link_id) params += `&link_id=${encodeURIComponent(link_id)}`
    if (property?.id) params += `&property_id=${encodeURIComponent(property.id)}`
    if (buyer_id) params += `&buyer_id=${encodeURIComponent(buyer_id)}`

    // Filter status flags
    if (filterStatus.this_week) params += `&this_week=true`
    if (filterStatus.lead_not_contacted) params += `&lead_not_contacted=true`
    if (filterStatus.total_lead_ytd) params += `&total_lead_ytd=true`
    if (filterStatus.new_lead_quarter) params += `&new_lead_quarter=true`

    if (sortDirection && sortBy !== "id") params += `&sort_direction=${sortDirection}`
    if (sortBy && sortBy !== "id") params += `&sort_by=${sortBy}`
    if (selectedUser?.value) params += `&show_user_id=${encodeURIComponent(selectedUser.value)}`

    return params
  }

  const buildAdvanceFiltersQuery = (advanceFilters = []) => {
    const validAdvanceFilters = advanceFilters.filter(group =>
      group.filters && group.filters.some(f => f.field && f.operator)
    )
    return encodeURIComponent(JSON.stringify(validAdvanceFilters))
  }

  const fetchLeads = (filters, advanceFilters = []) => {
    const queryParams = buildQueryParams(filters)
    const advanceFiltersQuery = buildAdvanceFiltersQuery(advanceFilters)

    setLoading(true)

    axios
      .get(`${BASE_URL}/list-leads?active_tab=${activeTab}${queryParams}&advance_filters=${advanceFiltersQuery}`, config)
      .then((res) => {
        const value = res?.data?.leadsList
        setLeadsData(value?.data || [])
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        })
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
      .finally(() => setLoading(false))
  }

  // Updated fetchDashboardData with advance filters support
  const fetchDashboardData = (sectionKey = "", page = 1, perPage = 100, advanceFilters = advanceFilterData) => {
    let url = `${BASE_URL}/lead-dashboard-list?active_tab=${activeTab}`

    const advanceFiltersQuery = buildAdvanceFiltersQuery(advanceFilters)
    url += `&advance_filters=${advanceFiltersQuery}`

    if (sectionKey) {
      url += `&section=${sectionKey}&page=${page}&per_page=${perPage}`
    } else {
      url += `&per_page=${perPage}`
    }

    console.log("Dashboard API URL:", url)

    axios.get(url, config)
      .then((res) => {
        setLeadDashboard(res.data)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message)
        }
      })
  }

  // ─── Dashboard Handlers ────────────────────────────────────────
  const handlePageChange = (sectionKey, newPage) => {
    const currentPerPage = pageSizes[sectionKey] || 100
    fetchDashboardData(sectionKey, newPage, currentPerPage)
  }

  const handlePageSizeChange = (sectionKey, newPerPage) => {
    setPageSizes(prev => ({ ...prev, [sectionKey]: newPerPage }))
    fetchDashboardData(sectionKey, 1, newPerPage) // Reset to page 1
  }

  // ─── Effects ───────────────────────────────────────────────────
  useEffect(() => {
    fetchLeads(filterData, advanceFilterData)
  }, [
    sortDirection, sortBy, filterStatus, activeTab, currentPage,
    itemPerPage, debouncedSearch, selectedUser, advanceFilterData
  ])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, activeTab, filterStatus, selectedUser, advanceFilterData])

  useEffect(() => {
    if ([1, 3].includes(activeTab)) {
      fetchDashboardData("", 1, 100) // Default 100
    }
  }, [activeTab, selectedUser, advanceFilterData])

  const onSuccess = (filters, advanceFilters) => {
    const normalFilters = filters || filterData
    const advFilters = advanceFilters !== undefined ? advanceFilters : advanceFilterData
    fetchLeads(normalFilters, advFilters)

    if ([1, 3].includes(activeTab)) {
      fetchDashboardData("", 1, 100)
    }
  }

  const handleStatusChange = (value, leadId) => {
    setRowStatuses((prev) => ({ ...prev, [leadId]: value }))

    const selectedOption = statusOptions.find((opt) => opt.value === value)

    axios
      .post(
        `${BASE_URL}/lead-status-change/${leadId}`,
        { lead_status: value, type: selectedOption?.label },
        config
      )
      .then((res) => {
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const handleDelete = () => {
    setDisableDelete(true)
    axios
      .delete(`${BASE_URL}/delete-lead/${selectedId}`, config)
      .then((res) => {
        setSelectedId("")
        onSuccess()
        setShowDeleteModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
      .finally(() => setDisableDelete(false))
  }

  const handleConfirm = () => {
    axios
      .post(`${BASE_URL}/markAs-Proposal/${selectedId}`, { mark_as_proposal: true }, config)
      .then((res) => {
        onSuccess()
        setShowConfirmModal(false)
        if (res?.data?.message) NotificationManager.success(res.data.message)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const fetchUsers = () => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=users`, config)
      .then((res) => {
        const options = res?.data?.data?.map((el) => ({
          value: el.id,
          label: `${el.first_name} ${el.last_name}`,
          role: el.role_id,
        }))
        setBdsOptions(options)
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response.data.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response.data.message)
        }
      })
  }

  const leadTypeValue = selectedLeadType === 0 ? "Disposition" : "Acquisition"

  const viewOptions = [
    { value: 1, label: "My Leads" },
    { value: 3, label: "Team Leads" },
  ]

  const handleViewChange = (e) => {
    const newTab = parseInt(e.target.value)
    setActiveTab(newTab)
    setCurrentPage(1)
    setExpandedRows({})
    setFilterStatus({
      this_week: false,
      lead_not_contacted: false,
      total_lead_ytd: false,
      new_lead_quarter: false,
    })
  }

  useEffect(() => {
    fetchUsers()
  }, [])

  const handleBoxClick = (statusKey) => {
    const targetTab = activeTab === 0 ? 1 : 3
    setActiveTab(targetTab)
    setCurrentPage(1)
    setFilterStatus({
      this_week: false,
      lead_not_contacted: false,
      total_lead_ytd: false,
      new_lead_quarter: false,
      [statusKey]: true,
    })
  }

  const handleUserSwitch = (option) => {
    if (!option) {
      setSelectedUser(null)
      setIsSwitchingUser(false)
      onSuccess()
      return
    }

    if (!isSwitchingUser) {
      setOriginalUserData(leadsData)
      setIsSwitchingUser(true)
    }

    setSelectedUser(option)
    onSuccess()
  }

  const getDisplayText = (el) => {
    if (el?.lead_type === 0 && el.portfolio !== 1) {
      if (!el?.link?.property_name || !el?.link_address_name) return ""
      return (
        <>
          <span>{el.link.property_name}</span>
          <br />
          <span>{el.link_address_name.street}</span>
          <br />
          <span>{el.link_address_name.city}, {el.link_address_name.state}</span>
        </>
      )
    }
    if (el?.lead_type === 1 && el.portfolio !== 1) {
      return el?.link?.property_type?.[0]?.type || ""
    }
    return el?.link?.name || ""
  }

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">Leads</p>
        <div className="md:flex block gap-4 items-center">
          <div className="flex gap-4 justify-between items-center w-full md:w-auto flex-wrap">
            <div className="relative min-w-[240px]">
              <select
                value={activeTab}
                onChange={handleViewChange}
                className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                <img src={DownArrow} alt="down" className="w-4 h-4" />
              </div>
            </div>

            <button
              onClick={() => setShowAddModal(true)}
              className="add-contact-button green-bg-H light-L body-S"
            >
              <img className="mr-2" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      <div className="flex block gap-4 items-center mb-4">
        <LeadAdvanceFilter
          filterData={advanceFilterData}
          onSetFilterData={setAdvanceFilterData}
          onCallApiAgain={(newAdvanceFilters) => {
            setAdvanceFilterData(newAdvanceFilters)
            fetchLeads(filterData, newAdvanceFilters)
          }}
        />

        <div className="search-box contacts relative flex-1 min-w-[200px]">
          <input
            type="text"
            className="body-S w-full"
            placeholder="Search name, contact count, duration, touches..."
            value={search}
            onChange={handleSearchChange}
            style={{ border: "1px solid #D8D8D8" }}
          />
        </div>
      </div>


      {(activeTab === 1 || activeTab === 3) && (
        <LeadsDashboard
          dashboardData={leadDashboard}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onRefresh={onSuccess}
        />
      )}

      <AddLeadModal from="Lead" showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />

      <EditLeadModal
        showModal={showEditModal}
        onClose={() => setShowEditModal(false)}
        id={selectedId}
        onCallApi={() => {
          setSelectedId("")
          onSuccess()
        }}
        from="Lead"
      />

      <ConvertPipelineModal
        showModal={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        handleConfirm={handleConfirm}
        from="lead"
        id={selectedId}
        leadType={leadTypeValue}
      />

      <DeleteConfirmationModal
        showModal={showDeleteModal}
        disable={disableDelete}
        onClose={() => setShowDeleteModal(false)}
        handleDelete={handleDelete}
      />

      {showColumnModal && (
        <ColumnCustomizer
          allColumns={allColumns}
          visibleColumns={visibleColumns}
          setVisibleColumns={setVisibleColumns}
          storageKey="leadsVisibleColumns"
          onClose={() => setShowColumnModal(false)}
        />
      )}
    </BaseLayout>
  )
}

export default Leads